import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { SelectOption } from '../../../components/SelectDropdown/SelectDropdown'

import { User } from '../../../models/User'
import { formatDate } from '../../../common/dateUtils'

export const MAX_DOCUMENT_BATCH_FILES = 10

export const MAX_DOCUMENT_FILE_SIZE_BYTES = 20 * 1024 * 1024

export enum DocumentUploadSource {
  ZOCKS = 'zocks',
  INTEGRATION = 'integration',
  EMAIL_ATTACHMENT = 'emailAttachment',
}

export const DOCUMENT_UPLOAD_SOURCE_LABEL: Record<DocumentUploadSource, string> = {
  [DocumentUploadSource.ZOCKS]: 'manual_upload',
  [DocumentUploadSource.INTEGRATION]: 'integration',
  [DocumentUploadSource.EMAIL_ATTACHMENT]: 'email_attachment',
}

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
}

export const inferContentType = (name: string): string => {
  const extension = name.split('.').pop()?.toLowerCase()
  return (extension && CONTENT_TYPE_BY_EXTENSION[extension]) || 'application/octet-stream'
}

export const zocksUserToSelectOption = (user: User): SelectOption => {
  const displayName = getUserDisplayName(user)
  return {
    id: user.id,
    label: `${displayName} (${user.email})`,
  }
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B'
  }

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export const hashFile = async (file: File) => {
  const buffer = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export const isFileSizeValid = (bytes: number) => {
  return bytes > 0 && bytes <= MAX_DOCUMENT_FILE_SIZE_BYTES
}

const _MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24
const _RELATIVE_DATE_CUTOFF_DAYS = 7

export const formatEmailAttachmentDate = (createdAt: string): string => {
  const date = new Date(createdAt)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.round((today.getTime() - dateOnly.getTime()) / _MILLISECONDS_IN_A_DAY)

  if (diffDays <= 0) {
    return 'Today'
  }
  if (diffDays === 1) {
    return 'Yesterday'
  }
  if (diffDays < _RELATIVE_DATE_CUTOFF_DAYS) {
    return `${diffDays} days ago`
  }
  return formatDate(date)
}

export const truncateFilenameMiddle = (fileName: string, maxLength = 40): string => {
  if (fileName.length <= maxLength) {
    return fileName
  }

  const dotIndex = fileName.lastIndexOf('.')
  const hasExtension = dotIndex > 0 && dotIndex < fileName.length - 1
  const extension = hasExtension ? fileName.slice(dotIndex) : ''
  const base = hasExtension ? fileName.slice(0, dotIndex) : fileName

  const keep = maxLength - extension.length - 1
  if (keep <= 0) {
    return fileName
  }

  const head = Math.ceil(keep / 2)
  const tail = Math.floor(keep / 2)
  return `${base.slice(0, head)}…${tail > 0 ? base.slice(base.length - tail) : ''}${extension}`
}
